import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { AgeExemptionApplication, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const REGISTRAR_ROLES = ['SHIP_REGISTRAR'];
const STATES = ['APPLIED', 'SURVEYOR_APPOINTED', 'SURVEY_REPORT_SUBMITTED', 'BOARD_REVIEW', 'APPROVED', 'REJECTED'];

/**
 * M22 — the age-exemption ("LUMARA/MINERVA pathway, productised") route a
 * vessel enters when at/above the age threshold at doc review: surveyor
 * appointment -> survey report -> SMSA board decision -> exemption cert.
 * The linked survey has no standalone list endpoint — its report is
 * submitted here via POST /surveys/:id/report once SURVEYOR_APPOINTED, and
 * the application auto-advances to BOARD_REVIEW server-side.
 */
export function AgeExemptions() {
  const { persona } = useAuth();
  const isRegistrar = persona ? persona.roles.some((r) => REGISTRAR_ROLES.includes(r)) : false;
  const [status, setStatus] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    return `/api/v1/m22/age-exemptions?${params.toString()}`;
  }, [status]);

  const listQ = useApiQuery<Page<AgeExemptionApplication>>(path);

  return (
    <div>
      <PageHeader
        title="Age Exemptions"
        subtitle="Vessels at/above the age threshold: surveyor appointment, survey report, SMSA board decision (M22 — SMSA)."
        actions={
          <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All states</option>
            {STATES.map((s) => (
              <option key={s} value={s}>
                {s.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        }
      />

      {!isRegistrar ? <RoleNotice roles={REGISTRAR_ROLES} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No age exemption applications match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Vessel</th>
                <th className="table-head">Vessel age</th>
                <th className="table-head">State</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((a) => (
                <tr key={a.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(a.id)}>
                  <td className="table-cell font-mono text-xs font-semibold">{a.vesselRegistration?.regNo ?? a.vesselRegistrationId}</td>
                  <td className="table-cell text-xs text-slate-500">{a.vesselAgeYears} years</td>
                  <td className="table-cell">
                    <StatusBadge status={a.state} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedId ? (
        <AgeExemptionDetailModal
          id={selectedId}
          isRegistrar={isRegistrar}
          onClose={() => setSelectedId(null)}
          onChanged={() => listQ.reload()}
        />
      ) : null}
    </div>
  );
}

function AgeExemptionDetailModal({ id, isRegistrar, onClose, onChanged }: { id: string; isRegistrar: boolean; onClose: () => void; onChanged: () => void }) {
  const q = useApiQuery<AgeExemptionApplication>(`/api/v1/m22/age-exemptions/${id}`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [surveyorId, setSurveyorId] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [showAppoint, setShowAppoint] = useState(false);
  const [recommendation, setRecommendation] = useState<'APPROVE' | 'REJECT'>('APPROVE');
  const [findings, setFindings] = useState('');
  const [showReport, setShowReport] = useState(false);
  const [outcome, setOutcome] = useState<'APPROVED' | 'REJECTED' | null>(null);
  const [reason, setReason] = useState('');
  const [minutesRef, setMinutesRef] = useState('');

  const call = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      q.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  const a = q.data;

  return (
    <Modal title={a ? `Age exemption — ${a.vesselRegistration?.regNo ?? a.vesselRegistrationId}` : 'Age exemption'} onClose={onClose} wide>
      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {a ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            <Detail label="State" value={<StatusBadge status={a.state} />} />
            <Detail label="Vessel age" value={`${a.vesselAgeYears} years`} />
            {a.validUntil ? <Detail label="Exemption valid until" value={formatDate(a.validUntil)} /> : null}
            {a.boardDecisionReason ? <Detail label="Board decision" value={a.boardDecisionReason} /> : null}
          </div>

          {a.survey ? (
            <div className="rounded-lg border border-slate-100 p-3 text-sm">
              <div className="mb-1 flex items-center justify-between">
                <span className="font-medium">Linked survey</span>
                <StatusBadge status={a.survey.status} />
              </div>
              {a.survey.surveyorId ? <div className="text-xs text-slate-500">Surveyor: {a.survey.surveyorId}</div> : null}
              {a.survey.findings ? <div className="mt-1 text-xs text-slate-500">Findings: {a.survey.findings}</div> : null}
            </div>
          ) : null}

          {isRegistrar && a.state === 'APPLIED' ? (
            !showAppoint ? (
              <button className="btn-primary" onClick={() => setShowAppoint(true)}>
                Appoint surveyor
              </button>
            ) : (
              <div className="grid grid-cols-2 gap-3 rounded-lg border border-slate-200 p-3">
                <Field label="Surveyor id">
                  <input className="field-input" value={surveyorId} onChange={(e) => setSurveyorId(e.target.value)} />
                </Field>
                <Field label="Scheduled at">
                  <input className="field-input" type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
                </Field>
                <button
                  className="btn-primary col-span-2"
                  disabled={busy || !surveyorId.trim() || !scheduledAt}
                  onClick={() =>
                    call(() =>
                      api.post(`/api/v1/m22/age-exemptions/${a.id}/appoint-surveyor`, {
                        surveyorId: surveyorId.trim(),
                        scheduledAt: new Date(scheduledAt).toISOString(),
                      }),
                    )
                  }
                >
                  {busy ? 'Appointing…' : 'Confirm appointment'}
                </button>
              </div>
            )
          ) : null}

          {isRegistrar && a.state === 'SURVEYOR_APPOINTED' && a.survey ? (
            !showReport ? (
              <button className="btn-primary" onClick={() => setShowReport(true)}>
                Submit survey report
              </button>
            ) : (
              <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                <div className="flex gap-2">
                  <button className={`btn-success ${recommendation === 'APPROVE' ? 'ring-2 ring-offset-1' : ''}`} onClick={() => setRecommendation('APPROVE')}>
                    Recommend approve
                  </button>
                  <button className={`btn-danger ${recommendation === 'REJECT' ? 'ring-2 ring-offset-1' : ''}`} onClick={() => setRecommendation('REJECT')}>
                    Recommend reject
                  </button>
                </div>
                <Field label="Findings">
                  <textarea className="field-input" rows={2} value={findings} onChange={(e) => setFindings(e.target.value)} />
                </Field>
                <button
                  className={recommendation === 'APPROVE' ? 'btn-success' : 'btn-danger'}
                  disabled={busy || findings.trim().length < 3}
                  onClick={() => call(() => api.post(`/api/v1/m22/surveys/${a.survey!.id}/report`, { recommendation, findings: findings.trim() }))}
                >
                  {busy ? 'Submitting…' : 'Submit report (auto-advances to board review)'}
                </button>
              </div>
            )
          ) : null}

          {isRegistrar && a.state === 'BOARD_REVIEW' ? (
            <div className="space-y-2 rounded-lg border border-slate-200 p-3">
              <div className="text-sm font-semibold text-slate-700">SMSA board decision</div>
              <div className="flex gap-2">
                <button className={`btn-success ${outcome === 'APPROVED' ? 'ring-2 ring-offset-1' : ''}`} onClick={() => setOutcome('APPROVED')}>
                  Approve exemption
                </button>
                <button className={`btn-danger ${outcome === 'REJECTED' ? 'ring-2 ring-offset-1' : ''}`} onClick={() => setOutcome('REJECTED')}>
                  Reject
                </button>
              </div>
              {outcome ? (
                <>
                  <Field label="Reason">
                    <textarea className="field-input" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
                  </Field>
                  <Field label="Board minutes ref (optional)">
                    <input className="field-input" value={minutesRef} onChange={(e) => setMinutesRef(e.target.value)} />
                  </Field>
                  <button
                    className={outcome === 'APPROVED' ? 'btn-success' : 'btn-danger'}
                    disabled={busy || reason.trim().length < 3}
                    onClick={() =>
                      call(() =>
                        api.post(`/api/v1/m22/age-exemptions/${a.id}/board-decision`, {
                          outcome,
                          reason: reason.trim(),
                          ...(minutesRef.trim() ? { minutesRef: minutesRef.trim() } : {}),
                        }),
                      )
                    }
                  >
                    {busy ? 'Submitting…' : `Confirm ${outcome.toLowerCase()}`}
                  </button>
                </>
              ) : null}
            </div>
          ) : null}

          {!isRegistrar ? <RoleNotice roles={REGISTRAR_ROLES} /> : null}

          <div className="border-t border-slate-100 pt-3 text-xs text-slate-400">Filed {formatDateTime(a.createdAt)}</div>
        </div>
      ) : null}
    </Modal>
  );
}
